import {Attributes, AttributesRow, Card, EmptyStatus} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';

import {find, isEmpty, isNil} from 'ramda';

import {CoreExchangeReturnToSupplierTab} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {SummaryItem, useGetVatRatesOptions, useTenant} from '@dms/shared';

import {EMPTY_PLACEHOLDER, Nullish, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CURRENCY_DECIMALS} from '../../../../../constants/CURRENCY_DECIMALS';
import {UserName} from './UserName';

interface ReturnToSupplierTabProps extends RequiredTestIdProps {
  returnToSupplier: CoreExchangeReturnToSupplierTab | Nullish;
  coreItemQuantity: number | Nullish;
  onEditSettings: VoidFunction;
  isEditable: boolean;
}

const SETTINGS_COLUMNS = 3;

export function ReturnToSupplierTab(props: ReturnToSupplierTabProps) {
  const tenant = useTenant();
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();
  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  const formatDate = (date: string | Nullish) => {
    if (isNil(date) || isEmpty(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDateTime('dateShort', parseDate(date));
  };

  const formatPrice = (price: number | Nullish) => {
    if (isNil(price)) {
      return null;
    }
    return formatCurrency(price, tenant.tenantCurrency, CURRENCY_DECIMALS);
  };

  const formatBoolean = (value: boolean | Nullish) => {
    if (isNil(value)) {
      return null;
    }
    return i18n.t(value ? 'general.labels.yes' : 'general.labels.no');
  };

  const settings = props.returnToSupplier?.settings;
  const authorization = props.returnToSupplier?.authorizationForReturn;
  const execution = props.returnToSupplier?.returnExecution;

  const vatRate = find((option) => option.type === settings?.returnVatRate, vatOptions);

  const settingsItems = [
    {
      id: 'transactionType',
      title: i18n.t('entity.warehouse.labels.coreExchangeReturnTransactionType'),
      value: settings?.returnTransactionType,
    },
    {
      id: 'returnDeadline',
      title: i18n.t('entity.warehouse.labels.coreExchangeReturnDeadline'),
      value: isNil(settings?.returnDeadline) ? null : formatDate(settings.returnDeadline),
    },
    {
      id: 'quantity',
      title: i18n.t('entity.warehouse.labels.quantity'),
      value: props.coreItemQuantity?.toString(),
    },
    {
      id: 'unitPrice',
      title: i18n.t('entity.warehouse.labels.unitPrice'),
      value: formatPrice(settings?.returnUnitPrice),
    },
    {
      id: 'vatRate',
      title: i18n.t('general.labels.vatRate'),
      value: isNil(vatRate) ? settings?.returnVatRate : vatRate.label,
    },
    {
      id: 'discount',
      title: i18n.t('entity.warehouse.labels.coreExchangeReturnDiscount'),
      value: isNil(settings?.returnDiscount) ? null : `${settings.returnDiscount} %`,
    },
    {
      id: 'processingFee',
      title: i18n.t('entity.warehouse.labels.coreExchangeReturnProcessingFeeWithoutVat'),
      value: formatPrice(settings?.returnProcessingFee),
    },
    {
      id: 'vatValue',
      title: i18n.t('entity.warehouse.labels.vat'),
      value: formatPrice(settings?.returnVatValue),
    },
    {
      id: 'supplierDiscountGroup',
      title: i18n.t('entity.warehouse.labels.coreExchangeSupplierDiscountGroupCode'),
      value: settings?.supplierDiscountGroupCode,
    },
    {
      id: 'vehicleRequired',
      title: i18n.t('entity.warehouse.labels.coreExchangeVehicleRequiredBySupplier'),
      value: formatBoolean(settings?.returnIsVehicleRequired),
    },
    {
      id: 'totalWithoutVat',
      title: i18n.t('entity.warehouse.labels.coreExchangeReturnTotalWithoutVat'),
      value: formatPrice(settings?.returnTotalValueWithoutVat),
    },
    {
      id: 'totalWithVat',
      title: i18n.t('entity.lineItems.labels.totalPriceWithVat'),
      value: formatPrice(settings?.returnTotalValueWithVat),
    },
  ];

  const authorizationRows: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.coreExchangeRmaRequestDate'),
      value: formatDate(authorization?.rmaRequestDate),
    },
    {
      label: i18n.t('entity.warehouse.labels.coreExchangeRmaResponseDate'),
      value: formatDate(authorization?.rmaResponseDate),
    },
    {
      label: i18n.t('entity.warehouse.labels.coreExchangeRmaNumber'),
      value: authorization?.rmaNumber,
    },
    {
      label: i18n.t('entity.warehouse.labels.coreExchangeDeliveryNumber'),
      value: authorization?.deliveryNumber,
    },
    {
      label: i18n.t('entity.warehouse.labels.orderNumber'),
      value: authorization?.orderNumber,
    },
    {
      label: i18n.t('entity.warehouse.labels.note'),
      value: authorization?.rmaNote,
    },
  ];

  const executionRows: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.coreExchangeReturnedDate'),
      value: formatDate(execution?.returnedDate),
    },
    {
      label: i18n.t('general.labels.returnedBy'),
      content: (
        <UserName
          userId={execution?.returnedBy}
          data-testid={suffixTestId('execution.returnedBy', props)}
        />
      ),
    },
    {
      label: i18n.t('general.labels.document'),
      value: execution?.documentNumber,
    },
  ];

  const isReturned = !isNil(execution?.returnedDate);

  return (
    <VStack spacing={4}>
      <Card
        title={i18n.t('entity.warehouse.labels.coreExchangeReturnSettings')}
        actions={[
          {
            type: 'button',
            variant: 'link',
            leftIcon: 'image/edit',
            title: i18n.t('general.actions.edit'),
            onClick: props.onEditSettings,
            isDisabled: !props.isEditable,
            tooltip: props.isEditable ? null : i18n.t('general.labels.noPermission'),
            'data-testid': suffixTestId('settings.edit', props),
          },
        ]}
      >
        <Grid columns={SETTINGS_COLUMNS}>
          {settingsItems.map((item) => (
            <SummaryItem
              key={item.id}
              title={item.title}
              value={item.value}
              isAlternative
              data-testid={suffixTestId(`settings.${item.id}`, props)}
            />
          ))}
        </Grid>
      </Card>
      <Card title={i18n.t('entity.warehouse.labels.coreExchangeAuthorisationForReturn')}>
        <Attributes
          rows={authorizationRows}
          size="third"
          data-testid={suffixTestId('authorization', props)}
        />
      </Card>
      <Card title={i18n.t('entity.warehouse.labels.coreExchangeReturn')}>
        <Show when={isReturned}>
          <Attributes
            rows={executionRows}
            size="third"
            data-testid={suffixTestId('execution', props)}
          />
        </Show>
        <Show when={!isReturned}>
          <EmptyStatus
            headline={i18n.t('entity.warehouse.labels.coreExchangeNoReturnToSupplierYet')}
            data-testid={suffixTestId('execution.empty', props)}
          />
        </Show>
      </Card>
    </VStack>
  );
}
